import { useMemo, useState } from "react";
import MainLayout from "../../layout/MainLayout";
import { Button1, Button2, QuickActionSelect, Select } from "../index";
import plusIcon from "../../assets/icons/plus-circle-white.png";
import { useNavigate } from "react-router-dom";
import { useAllGroupExpenses } from "../../data/live";
import { socialPaymentsApi } from "../../api/endpoints";
import { message } from "antd";

const displayName = (user, fallback = "Not available") =>
  user?.displayName ||
  [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
  user?.email ||
  fallback;

const money = (amount, currency) =>
  `${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency || ""}`.trim();

const Reminder = () => {
  const navigate = useNavigate();
  const { items: expenses, loading } = useAllGroupExpenses();
  const [splitId, setSplitId] = useState("");
  const [sending, setSending] = useState(false);

  const unpaidSplits = useMemo(
    () =>
      expenses.flatMap((expense) =>
        (expense.splits || [])
          .filter((split) => !split.isPaid)
          .map((split) => ({
            ...split,
            expense,
          }))
      ),
    [expenses]
  );

  const selectedSplit =
    unpaidSplits.find((split) => split.id === splitId) || unpaidSplits[0];

  const sendReminder = async () => {
    if (!selectedSplit) return;
    setSending(true);
    try {
      await socialPaymentsApi.sendReminder(selectedSplit.id);
      message.success("Reminder sent");
    } catch (error) {
      message.error(error.response?.data?.error || "Unable to send reminder");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <MainLayout
        expand={false}
        NavName={"Split Payment"}
        navbarChildren={<QuickActionSelect className="rounded-5" />}
      >
        <div>
          <div className="container mt-5">
            <div className="row">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fs-5 fw-semibold">Reminder</span>
                <Button2
                  buttonName={"New Group"}
                  divClassName={"gap-2"}
                  className={"btn-dark"}
                  icon={<img src={plusIcon} alt="add" />}
                  onClick={() => navigate("/create-group")}
                />
              </div>

              <div className="col-12">
                <div className="d-flex flex-column gap-3 rounded-3 border mt-3">
                  <div className="d-flex justify-content-between align-items-center border-bottom p-3 px-4 flex-wrap gap-3">
                    <span className="fw-semibold fs-5">
                      Send a Balance Reminder
                    </span>

                    {loading ? (
                      <span className="fs14px" style={{ color: "#7D7D7D" }}>
                        Loading open splits...
                      </span>
                    ) : selectedSplit ? (
                      <div className="d-flex align-items-center gap-4 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                          <span className="fs14px" style={{ color: "#7D7D7D" }}>
                            Send to:
                          </span>
                          <Select
                            className="rounded-pill w-auto py-1"
                            value={selectedSplit.id}
                            options={unpaidSplits.map((split) => ({
                              value: split.id,
                              name: displayName(split.user, split.userId),
                            }))}
                            onChange={(e) => setSplitId(e.target.value)}
                          />
                        </div>

                        <span className="fs14px" style={{ color: "#7D7D7D" }}>
                          Amount due:{" "}
                          <span style={{ color: "#FC3C42" }}>
                            {money(selectedSplit.amountOwed, selectedSplit.expense.currency)}
                          </span>
                        </span>
                      </div>
                    ) : (
                      <span className="fs14px" style={{ color: "#7D7D7D" }}>
                        No unpaid splits found.
                      </span>
                    )}
                  </div>

                  <div className="d-flex flex-column gap-4 rounded-3 border mx-4 m-3 px-4 p-3">
                    {selectedSplit ? (
                      <>
                        <span style={{ color: "#7D7D7D" }}>
                          This reminder will be sent to{" "}
                          {displayName(selectedSplit.user, selectedSplit.userId)}.
                        </span>
                        <span style={{ color: "#7D7D7D" }}>
                          Expense: {selectedSplit.expense.description}
                        </span>
                        <span style={{ color: "#7D7D7D" }}>
                          Group: {selectedSplit.expense.group?.name || "Not set"}
                        </span>
                        <div>
                          <span style={{ color: "#7D7D7D" }}>Regards,</span>
                          <br />
                          <span style={{ color: "#7D7D7D" }}>ARM Financial</span>
                        </div>
                      </>
                    ) : (
                      <span className="text-muted">No reminder preview available.</span>
                    )}
                  </div>

                  <div className="d-flex align-items-center gap-2 justify-content-end p-3 px-4">
                    <Button1 buttonName={"Share"} className={"border"} disabled />
                    <Button1
                      buttonName={"Save as Draft"}
                      className={"btn-outline-success"}
                      disabled
                    />
                    <Button1
                      buttonName={sending ? "Sending..." : "Send Email"}
                      className={"btn-dark"}
                      onClick={sendReminder}
                      disabled={sending || !selectedSplit}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    </>
  );
};

export default Reminder;
